import React, { useEffect, useRef, useState } from "react";
import { Box, Typography, IconButton, Tooltip } from "@mui/material";
import {
  ZoomIn,
  ZoomOut,
  FitScreen,
  Fullscreen,
  FullscreenExit,
} from "@mui/icons-material";
import * as docx from "docx-preview";
import {
  ViewerSkeleton,
  ViewerErrorState,
} from "../FileViewerStates/FileViewerStates";

const DocxViewer = ({ fileUrl, onScrolledToBottom, fillHeight = false }) => {
  const scrollRef = useRef(null);
  const viewerRef = useRef(null);
  const fullScreenRef = useRef(null);
  const [scale, setScale] = useState(1.0);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [hasReachedBottom, setHasReachedBottom] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    const loadDocx = async () => {
      try {
        const res = await fetch(fileUrl);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        if (cancelled || !viewerRef.current) return;
        viewerRef.current.innerHTML = "";
        await docx.renderAsync(blob, viewerRef.current, null, {
          className: "docx-preview",
          inWrapper: true,
          ignoreWidth: true,
          ignoreHeight: true,
          breakPages: false,
          useBase64URL: false,
          experimental: true,
        });
        if (!cancelled) setLoading(false);
      } catch (err) {
        console.error("Failed to load DOCX:", err);
        if (!cancelled) {
          setError(true);
          setLoading(false);
        }
      }
    };
    loadDocx();
    return () => {
      cancelled = true;
    };
  }, [fileUrl, retryKey]);

  // Reset scroll detection when scale changes
  useEffect(() => {
    setHasReachedBottom(false);
  }, [scale]);

  const handleScroll = () => {
    const el = scrollRef.current;
    const content = viewerRef.current;
    if (!el || !content || hasReachedBottom) return;

    // Check if we've reached the bottom of the scrollable area
    const isAtBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 10;

    if (isAtBottom) {
      setHasReachedBottom(true);
      if (onScrolledToBottom) onScrolledToBottom();
    }
  };

  const handleZoomIn = () => {
    setScale((prev) => Math.min(prev + 0.1, 2.0));
    setHasReachedBottom(false);
  };
  const handleZoomOut = () => {
    setScale((prev) => Math.max(prev - 0.1, 0.5));
    setHasReachedBottom(false);
  };
  const handleResetZoom = () => {
    setScale(1.0);
    setHasReachedBottom(false);
  };

  const toggleFullscreen = () => {
    const container = fullScreenRef.current;
    if (!document.fullscreenElement) {
      container.requestFullscreen().catch((err) => console.error(err));
      setIsFullScreen(true);
    } else {
      document.exitFullscreen();
      setIsFullScreen(false);
    }
  };

  return (
    <Box
      sx={{
        maxWidth: fillHeight ? "100%" : "1000px",
        margin: fillHeight ? 0 : "0 auto",
        height: fillHeight ? "100%" : "auto",
      }}
    >
      <Box
        ref={fullScreenRef}
        sx={{
          position: "relative",
          height: isFullScreen ? "100vh" : fillHeight ? "100%" : "500px",
          border: fillHeight ? "none" : "1px solid #C5C9CF",
          borderRadius: fillHeight ? 0 : "8px",
          overflowY: "hidden",
          overflowX: "auto",
          bgcolor: "#2c2c2c",
          marginY: fillHeight ? 0 : 4,
        }}
      >
        {/* Zoom Controls */}
        <Box
          sx={{
            position: "absolute",
            bottom: 8,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 10,
            bgcolor: "rgb(230, 230, 230, 0.85)",
            borderRadius: "20px",
            px: 2,
            py: 0.5,
            display: "flex",
            alignItems: "center",
            gap: 3,
          }}
        >
          <Tooltip title="Zoom Out">
            <IconButton onClick={handleZoomOut} size="small">
              <ZoomOut />
            </IconButton>
          </Tooltip>
          <Tooltip title="Zoom In">
            <IconButton onClick={handleZoomIn} size="small">
              <ZoomIn />
            </IconButton>
          </Tooltip>
          <Typography variant="body2">{Math.round(scale * 100)}%</Typography>
          <Tooltip title="Reset Zoom">
            <IconButton onClick={handleResetZoom} size="small">
              <FitScreen />
            </IconButton>
          </Tooltip>
          <Tooltip title="Toggle Fullscreen">
            <IconButton onClick={toggleFullscreen} size="small">
              {isFullScreen ? <FullscreenExit /> : <Fullscreen />}
            </IconButton>
          </Tooltip>
        </Box>

        {/* Scrollable Content */}
        <Box
          ref={scrollRef}
          onScroll={handleScroll}
          sx={{
            height: "100%",
            width: "auto",
            overflowY: "auto",
            bgcolor: error || loading ? "#fff" : "transparent",
          }}
        >
          {error ? (
            <ViewerErrorState onRetry={() => setRetryKey((k) => k + 1)} />
          ) : (
            <>
              {loading && <ViewerSkeleton />}
              <Box
                ref={viewerRef}
                style={{
                  display: loading ? "none" : "block",
                  transform: `scale(${scale})`,
                  transformOrigin: "top center",
                  backgroundColor: "#fff",
                  padding: "0",
                  margin: "0 auto",
                  width: "fit-content",
                  boxShadow: "0 0 6px rgba(0,0,0,0.2)",
                  borderRadius: "4px",
                  height: scale < 1 ? `${100 / scale}%` : "auto",
                }}
              />
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default DocxViewer;
